import {
  Vector3,
  Matrix4,
  BufferGeometry,
  Material,
  SpriteMaterial,
} from 'three';
import type { Box3, Object3D, Matrix4Tuple, Line, Mesh, Texture } from 'three';

import type {
  PointJson,
  Matrix3Json,
  Matrix4Json,
  Vector3Json,
  QuaternionJson,
} from './contracts/basic';

const TOLERANCE = 1e-4;

export const isParallel = (
  v1: Vector3 | undefined | null,
  v2: Vector3 | undefined | null,
  tol = TOLERANCE,
): boolean => v1 != null && v2 != null && v1.angleTo(v2) <= tol;

export const isAntiParallel = (
  v1: Vector3 | undefined | null,
  v2: Vector3 | undefined | null,
  tol = TOLERANCE,
): boolean => v1 != null && v2 != null && v1.angleTo(v2) >= Math.PI - tol;

export const isPerpendicular = (
  v1: Vector3 | undefined | null,
  v2: Vector3 | undefined | null,
  tol = TOLERANCE,
): boolean => v1 != null && v2 != null && v1.dot(v2) > -tol && v1.dot(v2) < tol;

export const isClose = (a: number, b: number, tol = TOLERANCE): boolean =>
  Math.abs(a - b) < tol;

export const isCloseVectors = (
  v1: Vector3 | undefined | null,
  v2: Vector3 | undefined | null,
  tol = TOLERANCE,
): boolean =>
  v1 != null &&
  v2 != null &&
  isClose(v1.x, v2.x, tol) &&
  isClose(v1.y, v2.y, tol) &&
  isClose(v1.z, v2.z, tol);

const _zeroVector = new Vector3();

export const isPointOnRay = (
  { direction, startPoint }: { direction: Vector3; startPoint: Vector3 },
  testPoint: Vector3,
  tol = TOLERANCE,
): boolean => {
  if (
    isCloseVectors(
      new Vector3().subVectors(testPoint, startPoint),
      _zeroVector,
      tol,
    )
  ) {
    return true;
  }
  const testVector = new Vector3().subVectors(testPoint, startPoint);
  return (
    isParallel(testVector, direction, tol) ||
    isAntiParallel(testVector, direction, tol)
  );
};

export const getMidpoint = (v1: Vector3, v2: Vector3): Vector3 =>
  new Vector3((v1.x + v2.x) / 2, (v1.y + v2.y) / 2, (v1.z + v2.z) / 2);

export const getHorizontalAlignmentVector = (
  axis: Vector3,
  center: Vector3,
): Vector3 => {
  const v = axis.clone();
  const c = center.clone();
  const L = c.length();
  let a;
  if (Math.abs(L) < 1e-6) {
    a = 0;
  } else {
    a = Math.acos(v.dot(c) / c.length() / v.length());
  }
  const orthogPoint = v.multiplyScalar(c.length() * Math.cos(a));
  return new Vector3().subVectors(c, orthogPoint);
};

export const getBoundingBoxDiagonal = (boundingBox: Box3): number =>
  new Vector3().subVectors(boundingBox.min, boundingBox.max).length();

//Test face equality by looking at vertex indices
//* As of threejs r125, cannot test equality by comparing Face3 objects because Face3 object does not exist on BufferGeometry
type Face = { a: number; b: number; c: number };
export const areFacesEqual = (face1: Face, face2: Face): boolean =>
  face1.a === face2.a && face1.b === face2.b && face1.c === face2.c;

//IMPORTANT: must treat identityTransform if used
export const identityTransform = new Matrix4(); //default is identity transform
const rawIdentityTransform: Matrix4Tuple = identityTransform.toArray();
export const isIdentityTransformArray = (candidate?: Matrix4Tuple): boolean =>
  !!candidate &&
  candidate.length === rawIdentityTransform.length &&
  candidate.every((term, index) => term === rawIdentityTransform[index]);

//Based on https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/floor#decimal_adjustment
export const round10 = (value: number, exp: number): number => {
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math.round(value);
  }
  const v = +value;
  const e = +exp;
  // If the value is not a number or the exp is not an integer...
  if (isNaN(v) || !(typeof e === 'number' && e % 1 === 0)) {
    return NaN;
  } else if (exp === 0) {
    return Math.round(v);
  }

  //Shift
  const [magnitude, exponent = 0] = v.toString().split('e');
  const adjustedValue = Math.round(+`${magnitude}e${+exponent - exp}`);
  //Shift back
  const [newMagnitude, newExponent = 0] = adjustedValue.toString().split('e');
  return +`${newMagnitude}e${+newExponent + exp}`;
};

export const zeroVector: Readonly<Vector3Json> = Object.freeze([0, 0, 0]);

//simple dot product when threejs is too much overhead
export const dot = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
): number => ax * bx + ay * by + az * bz;

//simple norm when threejs is too much overhead
export const norm = ([x, y, z]: Vector3Json): number =>
  Math.sqrt(x * x + y * y + z * z);

//simple normalize when threejs is too much overhead
export const normalize = (v: Vector3Json): Vector3Json => {
  const [x, y, z] = v;
  const n = Math.sqrt(x * x + y * y + z * z);
  return n === 1 ? v : [x / n, y / n, z / n];
};

export const normalizeToTarget = (
  v: Vector3Json,
  target: Vector3Json,
): typeof target => {
  const [x, y, z] = v;
  const n = Math.sqrt(x * x + y * y + z * z);
  target[0] = x / n;
  target[1] = y / n;
  target[2] = z / n;
  return target;
};

export const vecAdd = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
): Vector3Json => [ax + bx, ay + by, az + bz];

export const vecAddToTarget = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
  target: Vector3Json,
): typeof target => {
  target[0] = ax + bx;
  target[1] = ay + by;
  target[2] = az + bz;
  return target;
};

export const vecSub = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
): Vector3Json => [ax - bx, ay - by, az - bz];

export const vecSubToTarget = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
  target: Vector3Json,
): typeof target => {
  target[0] = ax - bx;
  target[1] = ay - by;
  target[2] = az - bz;
  return target;
};

export const distanceTo = (
  [ax, ay, az]: PointJson,
  [bx, by, bz]: PointJson,
): number => {
  const x = bx - ax;
  const y = by - ay;
  const z = bz - az;
  return Math.sqrt(x * x + y * y + z * z);
};

export const twoPi = Math.PI * 2;
export const halfPi = Math.PI / 2;
export const degToRad = Math.PI / 180;

export const angleTo = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
): number => {
  const normAB = Math.sqrt(
    (ax * ax + ay * ay + az * az) * (bx * bx + by * by + bz * bz),
  );
  if (normAB === 0) return halfPi;
  let cosTheta = (ax * bx + ay * by + az * bz) / normAB;
  //clamped between -1 and 1
  if (cosTheta < -1) {
    cosTheta = -1;
  } else if (cosTheta > 1) {
    cosTheta = 1;
  }
  return Math.acos(cosTheta);
};

//simple cross product when threejs is too much overhead
export const cross = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
): Vector3Json => [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];

export const crossToTarget = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
  target: Vector3Json,
): typeof target => {
  const rx = ay * bz - az * by;
  const ry = az * bx - ax * bz;
  const rz = ax * by - ay * bx;
  target[0] = rx;
  target[1] = ry;
  target[2] = rz;
  return target;
};

//simple normalized cross product when threejs is too much overhead
export const normCross = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
): Vector3Json => {
  const normAB = Math.sqrt(
    (ax * ax + ay * ay + az * az) * (bx * bx + by * by + bz * bz),
  );
  return [
    (ay * bz - az * by) / normAB,
    (az * bx - ax * bz) / normAB,
    (ax * by - ay * bx) / normAB,
  ];
};

export const normCrossToTarget = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
  target: Vector3Json,
): typeof target => {
  const normAB = Math.sqrt(
    (ax * ax + ay * ay + az * az) * (bx * bx + by * by + bz * bz),
  );
  const rx = (ay * bz - az * by) / normAB;
  const ry = (az * bx - ax * bz) / normAB;
  const rz = (ax * by - ay * bx) / normAB;
  target[0] = rx;
  target[1] = ry;
  target[2] = rz;
  return target;
};

//simple cross product when threejs is too much overhead
export const scaleVec = (
  scale: number,
  [ax, ay, az]: Vector3Json,
): Vector3Json => [ax * scale, ay * scale, az * scale];

export const scaleVecToTarget = (
  scale: number,
  [ax, ay, az]: Vector3Json,
  target: Vector3Json,
): typeof target => {
  target[0] = ax * scale;
  target[1] = ay * scale;
  target[2] = az * scale;
  return target;
};

export const identityMatrix3: Matrix3Json = Object.freeze([
  1, 0, 0, 0, 1, 0, 0, 0, 1,
]) as Matrix3Json;

export const identityMatrix4: Matrix4Json = Object.freeze([
  1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1,
]) as Matrix4Json;

//rotatePlaneFactory: Creates a rotation matrix to rotate content from a plane to a different plane
//See https://en.wikipedia.org/wiki/Rotation_matrix#Axis_and_angle
export const rotatePlaneFactory = (
  [mx, my, mz]: Vector3Json, //normal of current plane
  [nx, ny, nz]: Vector3Json, //normal of plane to rotate to
): Matrix3Json => {
  const normMN = Math.sqrt(
    (mx * mx + my * my + mz * mz) * (nx * nx + ny * ny + nz * nz),
  );

  //rotation axis: [x, y, z] = normCross(n, m) (reusing normMN when calculating cos below)
  const x = (my * nz - mz * ny) / normMN;
  const y = (mz * nx - mx * nz) / normMN;
  const z = (mx * ny - my * nx) / normMN;

  //Using dotproduct to calculate cos theta (the angle between m and n)
  const cos = (mx * nx + my * ny + mz * nz) / normMN;

  //Using pythagrean's theorem, to calculate sin theta
  const sin = Math.sqrt(1 - cos * cos);

  //calc const values for expressions used more than once to minimize calculations
  const oneMinusCos = 1 - cos;
  const zSin = z * sin;
  const xSin = x * sin;
  const ySin = y * sin;
  const xOneMinusCos = x * oneMinusCos;
  const yOneMinusCos = y * oneMinusCos;
  const zOneMinusCos = z * oneMinusCos;
  const xyOneMinusCos = x * yOneMinusCos;
  const yzOneMinusCos = y * zOneMinusCos;
  const zxOneMinusCos = z * xOneMinusCos;
  return [
    //prettier-ignore
    x * xOneMinusCos + cos,
    xyOneMinusCos - zSin,
    zxOneMinusCos + ySin,
    xyOneMinusCos + zSin,
    y * yOneMinusCos + cos,
    yzOneMinusCos - xSin,
    zxOneMinusCos - ySin,
    yzOneMinusCos + xSin,
    z * zOneMinusCos + cos,
  ];
};

//multiply a matrix by a vector, when threejs is too much overhead
export const matrix3Vec3Multiply = (
  //prettier-ignore
  [
    a11, a12, a13,
    a21, a22, a23,
    a31, a32, a33,
  ]: Matrix3Json,
  [x, y, z]: Vector3Json,
): Vector3Json => [
  a11 * x + a12 * y + a13 * z,
  a21 * x + a22 * y + a23 * z,
  a31 * x + a32 * y + a33 * z,
];

export const matrix4Vec3Multiply = (
  //prettier-ignore
  [
    a11, a12, a13, a14,
    a21, a22, a23, a24,
    a31, a32, a33, a34,
    a41, a42, a43, a44,
  ]: Matrix4Json,
  [x, y, z]: Vector3Json,
): Vector3Json => {
  const w = 1 / (a41 * x + a42 * y + a43 * z + a44);
  return [
    (a11 * x + a12 * y + a13 * z + a14) * w,
    (a21 * x + a22 * y + a23 * z + a24) * w,
    (a31 * x + a32 * y + a33 * z + a34) * w,
  ];
};

export const matrix3Multiply = (
  //prettier-ignore
  [
    a11, a12, a13,
    a21, a22, a23,
    a31, a32, a33,
  ]: Matrix3Json,

  //prettier-ignore
  [
    b11, b12, b13,
    b21, b22, b23,
    b31, b32, b33,
  ]: Matrix3Json,
): Matrix3Json => [
  //row1
  a11 * b11 + a12 * b21 + a13 * b31, //c11
  a11 * b12 + a12 * b22 + a13 * b32, //c12
  a11 * b13 + a12 * b23 + a13 * b33, //c13

  //row2
  a21 * b11 + a22 * b21 + a23 * b31, //c21
  a21 * b12 + a22 * b22 + a23 * b32, //c22
  a21 * b13 + a22 * b23 + a23 * b33, //c23

  //row3
  a31 * b11 + a32 * b21 + a33 * b31, //c31
  a31 * b12 + a32 * b22 + a33 * b32, //c32
  a31 * b13 + a32 * b23 + a33 * b33, //c33
];

export const matrix3Det = (
  //prettier-ignore
  [
    a11, a12, a13,
    a21, a22, a23,
    a31, a32, a33,
  ]: Matrix3Json,
): number =>
  a11 * (a22 * a33 - a23 * a32) +
  a12 * (a23 * a31 - a21 * a33) +
  a13 * (a21 * a32 - a22 * a31);
//a11 * a22 * a33 - a11 * a32 * a23 - a21 * a12 * a33 + a21 * a32 * a13 + a31 * a12 * a23 - a31 * a22 * a13;

export const matrix4Multiply = (
  //prettier-ignore
  [
    a11, a12, a13, a14,
    a21, a22, a23, a24,
    a31, a32, a33, a34,
    a41, a42, a43, a44,
  ]: Matrix4Json,

  //prettier-ignore
  [
    b11, b12, b13, b14,
    b21, b22, b23, b24,
    b31, b32, b33, b34,
    b41, b42, b43, b44,
  ]: Matrix4Json,
): Matrix4Json => [
  //row1
  a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41, //c11
  a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42, //c12
  a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43, //c13
  a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44, //c14

  //row2
  a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41, //c21
  a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42, //c22
  a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43, //c23
  a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44, //c24

  //row3
  a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41, //c31
  a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42, //c32
  a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43, //c33
  a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44, //c34

  //row4
  a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41, //c41
  a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42, //c42
  a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43, //c43
  a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44, //c44
];

export const matrix4MultiplyChain = (
  ...transforms: Matrix4Json[]
): Matrix4Json => {
  const [first, ...ts] = transforms;
  let r: Matrix4Json = first;
  for (const t of ts) {
    r = matrix4Multiply(t, r);
  }
  return r;
};

export const matrix4Det = (
  //prettier-ignore
  [
    a11, a12, a13, a14,
    a21, a22, a23, a24,
    a31, a32, a33, a34,
    a41, a42, a43, a44,
  ]: Matrix4Json,
): number =>
  a41 *
    (+a14 * a23 * a32 -
      a13 * a24 * a32 -
      a14 * a22 * a33 +
      a12 * a24 * a33 +
      a13 * a22 * a34 -
      a12 * a23 * a34) +
  a42 *
    (+a11 * a23 * a34 -
      a11 * a24 * a33 +
      a14 * a21 * a33 -
      a13 * a21 * a34 +
      a13 * a24 * a31 -
      a14 * a23 * a31) +
  a43 *
    (+a11 * a24 * a32 -
      a11 * a22 * a34 -
      a14 * a21 * a32 +
      a12 * a21 * a34 +
      a14 * a22 * a31 -
      a12 * a24 * a31) +
  a44 *
    (-a13 * a22 * a31 -
      a11 * a23 * a32 +
      a11 * a22 * a33 +
      a13 * a21 * a32 -
      a12 * a21 * a33 +
      a12 * a23 * a31);

//compose matrix4 from translation, rotation and scale. (Applied as scale, rotate and finally translate)
export const matrix4Compose = (
  [tx, ty, tz]: Vector3Json, //translation
  [rx, ry, rz, rw]: QuaternionJson, //rotation
  [sx, sy, sz]: Vector3Json, //scale
): Matrix4Json => {
  const rx2 = rx + rx;
  const ry2 = ry + ry;
  const rz2 = rz + rz;

  //prettier-ignore
  const rxx = rx * rx2;
  const rxy = rx * ry2;
  const rxz = rx * rz2;
  const ryy = ry * ry2;
  const ryz = ry * rz2;
  const rzz = rz * rz2;
  const rwx = rw * rx2;
  const rwy = rw * ry2;
  const rwz = rw * rz2;

  //prettier-ignore
  return [
		( 1 - ( ryy + rzz ) ) * sx, ( rxy - rwz ) * sy,         ( rxz + rwy ) * sz,          tx,
		( rxy + rwz ) * sx,         ( 1 - ( rxx + rzz ) ) * sy, ( ryz - rwx ) * sz,          ty,
		( rxz - rwy ) * sx,         ( ryz + rwx ) * sy,         ( 1 - ( rxx + ryy ) ) * sz,  tz,
		0,                          0,                          0,                           1,
  ];
};

export const matrix4DecomposeScale = (
  //prettier-ignore
  [
    a11, a12, a13, a14,
    a21, a22, a23, a24,
    a31, a32, a33, a34,
    a41, a42, a43, a44,
  ]: Matrix4Json,
): Vector3Json => {
  const det =
    a41 *
      (+a14 * a23 * a32 -
        a13 * a24 * a32 -
        a14 * a22 * a33 +
        a12 * a24 * a33 +
        a13 * a22 * a34 -
        a12 * a23 * a34) +
    a42 *
      (+a11 * a23 * a34 -
        a11 * a24 * a33 +
        a14 * a21 * a33 -
        a13 * a21 * a34 +
        a13 * a24 * a31 -
        a14 * a23 * a31) +
    a43 *
      (+a11 * a24 * a32 -
        a11 * a22 * a34 -
        a14 * a21 * a32 +
        a12 * a21 * a34 +
        a14 * a22 * a31 -
        a12 * a24 * a31) +
    a44 *
      (-a13 * a22 * a31 -
        a11 * a23 * a32 +
        a11 * a22 * a33 +
        a13 * a21 * a32 -
        a12 * a21 * a33 +
        a12 * a23 * a31);
  const sx = Math.sqrt(a11 * a11 + a21 * a21 + a31 * a31);
  return [
    det < 0 ? -sx : sx,
    Math.sqrt(a12 * a12 + a22 * a22 + a32 * a32),
    Math.sqrt(a13 * a13 + a23 * a23 + a33 * a33),
  ];
};

export const matrix4DecomposeTranslation = (a: Matrix4Json): Vector3Json => [
  a[3],
  a[7],
  a[11],
];

export const matrix4DecomposeRotationAndScale = (
  a: Matrix4Json,
): [QuaternionJson, Vector3Json] => {
  const [sx, sy, sz] = matrix4DecomposeScale(a);

  //rotation matrix
  const r11 = a[0] / sx;
  const r21 = a[4] / sx;
  const r31 = a[8] / sx;
  const r12 = a[1] / sy;
  const r22 = a[5] / sy;
  const r32 = a[9] / sy;
  const r13 = a[2] / sz;
  const r23 = a[6] / sz;
  const r33 = a[10] / sz;

  const trace = r11 + r22 + r33;
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0);
    return [
      [(r32 - r23) * s, (r13 - r31) * s, (r21 - r12) * s, 0.25 / s],
      [sx, sy, sz],
    ];
  } else if (r11 > r22 && r11 > r33) {
    const s = 2.0 * Math.sqrt(1.0 + r11 - r22 - r33);
    return [
      [0.25 * s, (r12 + r21) / s, (r13 + r31) / s, (r32 - r23) / s],
      [sx, sy, sz],
    ];
  } else if (r22 > r33) {
    const s = 2.0 * Math.sqrt(1.0 + r22 - r11 - r33);
    return [
      [(r12 + r21) / s, 0.25 * s, (r23 + r32) / s, (r13 - r31) / s],
      [sx, sy, sz],
    ];
  } else {
    const s = 2.0 * Math.sqrt(1.0 + r33 - r11 - r22);
    return [
      [(r13 + r31) / s, (r23 + r32) / s, 0.25 * s, (r21 - r12) / s],
      [sx, sy, sz],
    ];
  }
};

export const quaternionFromAxisAngle = (
  [x, y, z]: Vector3Json,
  angle: number,
): QuaternionJson => {
  const halfAngle = angle / 2;
  const s = Math.sin(halfAngle);
  return [x * s, y * s, z * s, Math.cos(halfAngle)];
};

export const quaternionFromUnitVectors = (
  [ax, ay, az]: Vector3Json,
  [bx, by, bz]: Vector3Json,
) => {
  let r = ax * bx + ay * by + az * bz + 1;
  if (r < Number.EPSILON) {
    //vectors point in opposite directions
    r = 0;
    if (Math.abs(ax) > Math.abs(az)) {
      return [ay === 0 ? 0 : -ay, ax, 0, r];
    } else {
      return [0, ax === 0 ? 0 : -ax, ay, r];
    }
  }
  return [
    //cross product
    ay * bz - az * by,
    az * bx - ax * bz,
    ax * by - ay * bx,
    r,
  ];
};

//simple quaternion dot product when threejs is too much overhead
export const qDot = (
  [ax, ay, az, aw]: QuaternionJson,
  [bx, by, bz, bw]: QuaternionJson,
): number => ax * bx + ay * by + az * bz + aw * bw;

//simple quaternion norm when threejs is too much overhead
export const qNorm = ([x, y, z, w]: QuaternionJson): number =>
  Math.sqrt(x * x + y * y + z * z + w * w);

//simple normalize when threejs is too much overhead
export const qNormalize = (q: QuaternionJson): QuaternionJson => {
  const [x, y, z, w] = q;
  const n = Math.sqrt(x * x + y * y + z * z + w * w);
  return n === 0 ? [0, 0, 0, 1] : n === 1 ? q : [x / n, y / n, z / n, w / n];
};

export const qConjugate = ([x, y, z, w]: QuaternionJson): QuaternionJson => [
  -x,
  -y,
  -z,
  w,
];

export const qScale = (
  [x, y, z, w]: QuaternionJson,
  s: number,
): QuaternionJson => [x * s, y * s, z * s, w * s];

export const qAdd = (
  [ax, ay, az, aw]: QuaternionJson,
  [bx, by, bz, bw]: QuaternionJson,
): QuaternionJson => [ax + bx, ay + by, az + bz, aw + bw];

export const qMultiply = (
  [ax, ay, az, aw]: QuaternionJson,
  [bx, by, bz, bw]: QuaternionJson,
): QuaternionJson => [
  ax * bw + aw * bx + ay * bz - az * by,
  ay * bw + aw * by + az * bx - ax * bz,
  az * bw + aw * bz + ax * by - ay * bx,
  aw * bw - ax * bx - ay * by - az * bz,
];

type IPoint<SPACE extends 2 | 3> = SPACE extends 2
  ? [number, number]
  : [number, number, number];

/**
 * bspline()
 * Based on https://github.com/thibauts/b-spline (no longer supported)
 * MIT license, Copyright (c) 2015 Thibaut Séguy <thibaut.seguy@gmail.com>
 */
export const bspline = <SPACE extends 2 | 3>(
  t: number, //position along the curve in the [0, 1] range
  degree: number, //degree of the curve. Must be less than or equal to the number of control points minus 1. 1 is linear, 2 is quadratic, 3 is cubic, and so on.
  points: IPoint<SPACE>[], //control points that will be interpolated. Can be vectors of any dimensionality ([x, y], [x, y, z], ...)
  knots?: number[], //Allow to modulate the control points interpolation spans on t. Must be a non-decreasing sequence of number of points + degree + 1 length values.
  weights?: number[], //control points weights. Must be the same length as the control point array.
): IPoint<SPACE> => {
  const n = points.length; // points count
  const d = points[0].length; // point dimensionality

  if (t < 0 || t > 1) {
    throw new Error('t out of bounds [0,1]: ' + t);
  }
  if (degree < 1) throw new Error('degree must be at least 1 (linear)');
  if (degree > n - 1)
    throw new Error('degree must be less than or equal to point count - 1');

  if (!weights) {
    // build weight vector of length [n]
    //eslint-disable-next-line no-param-reassign
    weights = [];
    for (let i = 0; i < n; i++) {
      weights[i] = 1;
    }
  }

  if (!knots) {
    //build knot vector of length [n + degree + 1]
    //eslint-disable-next-line no-param-reassign
    knots = [];
    for (let i = 0; i < n + degree + 1; i++) {
      knots[i] = i;
    }
  } else {
    if (knots.length !== n + degree + 1)
      throw new Error('bad knot vector length');
  }

  const domain: [number, number] = [degree, knots.length - 1 - degree];

  // remap t to the domain where the spline is defined
  const low: number = knots[domain[0]];
  const high: number = knots[domain[1]];
  //eslint-disable-next-line no-param-reassign
  t = t * (high - low) + low;

  // Clamp to the upper &  lower bounds instead of
  // throwing an error like in the original lib
  // https://github.com/bjnortier/dxf/issues/28
  //eslint-disable-next-line no-param-reassign
  t = Math.max(t, low);
  //eslint-disable-next-line no-param-reassign
  t = Math.min(t, high);

  // find s (the spline segment) for the [t] value provided
  let s: number;
  for (s = domain[0]; s < domain[1]; s++) {
    if (t >= knots[s] && t <= knots[s + 1]) {
      break;
    }
  }

  // convert points to homogeneous coordinates
  const v: number[][] = [];
  for (let i = 0; i < n; i++) {
    v[i] = [];
    for (let j = 0; j < d; j++) {
      v[i][j] = points[i][j] * weights[i];
    }
    v[i][d] = weights[i];
  }

  // l (level) goes from 1 to the curve degree + 1
  let alpha: number;
  for (let l = 1; l <= degree + 1; l++) {
    // build level l of the pyramid
    for (let i = s; i > s - degree - 1 + l; i--) {
      alpha = (t - knots[i]) / (knots[i + degree + 1 - l] - knots[i]);

      // interpolate each component
      for (let j = 0; j < d + 1; j++) {
        v[i][j] = (1 - alpha) * v[i - 1][j] + alpha * v[i][j];
      }
    }
  }

  // convert back to cartesian and return
  const result = [] as unknown as IPoint<SPACE>;
  for (let i = 0; i < d; i++) {
    result[i] = round10(v[s][i] / v[s][d], -9);
  }
  return result;
};

type DisposableThreeJS =
  | (
      | ((Object3D | BufferGeometry | Material | Line | Mesh | Texture) & {
          dispose?(): void;
          geometry?: BufferGeometry;
          material?: Material | Material[];
        })
      | Map<unknown, DisposableThreeJS>
      | DisposableThreeJS[]
    )
  | undefined;

//Recursively dispose of children
export const disposeThreeJS = (...objectsStack: DisposableThreeJS[]) => {
  let child: DisposableThreeJS;
  while ((child = objectsStack.pop())) {
    if (child) {
      if (Array.isArray(child)) {
        for (const v of child.values()) {
          objectsStack.push(v);
        }
        child.length = 0;
        continue;
      }
      if (child instanceof Map) {
        for (const v of child.values()) {
          objectsStack.push(v);
        }
        child.clear();
        continue;
      }
      if (typeof (child as Object3D).removeFromParent === 'function') {
        //may already be removed by previous iteration of while loop
        (child as Object3D).removeFromParent();
      }
      if ((child as Object3D).children) {
        objectsStack.push(...(child as Object3D).children);
        (child as Object3D).clear();
      }
      const { geometry, material } = child as Object3D & {
        geometry?: BufferGeometry;
        material?: Material | Material[];
      };
      if (geometry instanceof BufferGeometry) {
        objectsStack.push(geometry);
      }
      if (material instanceof Material) {
        if (material instanceof SpriteMaterial && material.map) {
          objectsStack.push(material.map);
        }
        objectsStack.push(material);
      } else if (Array.isArray(material)) {
        objectsStack.push(...material);
      }
      if (typeof child.dispose === 'function') {
        child.dispose();
      }
    }
  }
};
